import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, apiError, imgUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, FileText, Presentation, Trash2, X, GripVertical, Wand2, ShoppingCart, CheckCircle2 } from "lucide-react";
import { TechpackWizard } from "../components/TechpackWizard";

const emptySupplier = { name: "", contact_person: "", address: "", email: "", phone: "" };

export default function CollectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [collection, setCollection] = useState(null);
  const [exporting, setExporting] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [showTechpackWizard, setShowTechpackWizard] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showPoDialog, setShowPoDialog] = useState(false);
  const [poSupplier, setPoSupplier] = useState(emptySupplier);
  const [creatingPo, setCreatingPo] = useState(false);

  const toggleSelected = (itemId) => {
    const next = new Set(selectedItems);
    if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
    setSelectedItems(next);
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedItems(new Set());
  };

  const openPoDialog = () => {
    if (selectedItems.size === 0) {
      toast.error("Select at least one style first");
      return;
    }
    setShowPoDialog(true);
  };

  const createPo = async () => {
    if (!poSupplier.name.trim()) {
      toast.error("Supplier name is required");
      return;
    }
    setCreatingPo(true);
    try {
      const { data } = await api.post("/purchase-orders", {
        collection_id: id,
        item_ids: Array.from(selectedItems),
        supplier: poSupplier,
      });
      toast.success(`PO ${data.po_number} created`);
      navigate(`/purchase-orders/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCreatingPo(false);
    }
  };

  const load = () => api.get(`/collections/${id}`).then((r) => setCollection(r.data)).catch(() => navigate("/collections"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/collections/${id}/export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collection.name}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting("");
    }
  };

  const removeItem = async (itemId) => {
    try {
      await api.delete(`/collections/${id}/items/${itemId}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteCollection = async () => {
    if (!window.confirm("Delete this collection?")) return;
    await api.delete(`/collections/${id}`);
    navigate("/collections");
  };

  const handleDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) { setDragIndex(null); return; }
    const reordered = [...collection.items];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setCollection({ ...collection, items: reordered });
    setDragIndex(null);
    try {
      await api.patch(`/collections/${id}/reorder`, { item_ids: reordered.map((it) => it.item_id) });
    } catch (e) {
      toast.error(apiError(e));
      load();
    }
  };

  if (!collection) return <div className="p-10 text-sm text-zinc-400">Loading…</div>;

  const canManage = user?.role === "admin" || user?.id === collection.owner_id;

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <button data-testid="collection-back-button" onClick={() => navigate("/collections")} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950 transition-colors">
        <ArrowLeft size={14} /> Collections
      </button>
      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Lookbook</p>
          <h1 className="font-heading text-4xl font-bold mt-1 tracking-tight" data-testid="collection-title">{collection.name}</h1>
          {collection.description && <p className="text-zinc-500 mt-2 text-sm max-w-xl">{collection.description}</p>}
          <p className="text-xs text-zinc-400 mt-2">{collection.items.length} look{collection.items.length !== 1 ? "s" : ""} · curated by {collection.owner_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="collection-generate-techpacks" onClick={() => setShowTechpackWizard(true)} disabled={collection.items.length === 0}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
            <Wand2 size={14} /> Generate techpacks
          </button>
          {!selectMode ? (
            <button data-testid="collection-create-po" onClick={() => setSelectMode(true)} disabled={collection.items.length === 0}
              className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-40">
              <ShoppingCart size={14} /> Create Purchase Order
            </button>
          ) : (
            <>
              <button data-testid="collection-po-continue" onClick={openPoDialog} disabled={selectedItems.size === 0}
                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                <ShoppingCart size={14} /> Continue ({selectedItems.size})
              </button>
              <button data-testid="collection-po-cancel" onClick={exitSelectMode}
                className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors">
                Cancel
              </button>
            </>
          )}
          <button data-testid="collection-export-pdf" onClick={() => exportFile("pdf")} disabled={!!exporting || collection.items.length === 0}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-40">
            <FileText size={14} /> {exporting === "pdf" ? "Exporting…" : "Export PDF"}
          </button>
          <button data-testid="collection-export-pptx" onClick={() => exportFile("pptx")} disabled={!!exporting || collection.items.length === 0}
            className="inline-flex items-center gap-2 border border-zinc-300 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-100 transition-colors disabled:opacity-40">
            <Presentation size={14} /> {exporting === "pptx" ? "Exporting…" : "Export PPTX"}
          </button>
          {canManage && (
            <button data-testid="collection-delete-button" onClick={deleteCollection} className="p-2.5 border border-zinc-300 rounded-md hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
          )}
        </div>
      </div>

      {collection.items.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="collection-empty-state">
          <p className="text-sm">Empty collection. Open a design and use "Add to collection".</p>
          <Link to="/designs" className="mt-4 inline-block bg-zinc-950 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">Browse designs</Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collection.items.map((it, i) => (
            <div key={it.item_id} data-testid={`collection-item-${it.item_id}`}
              draggable={!selectMode}
              onDragStart={() => !selectMode && setDragIndex(i)}
              onDragOver={(e) => !selectMode && e.preventDefault()}
              onDrop={() => !selectMode && handleDrop(i)}
              onDragEnd={() => setDragIndex(null)}
              onClick={selectMode ? () => toggleSelected(it.item_id) : undefined}
              className={`group relative border bg-white rounded-lg overflow-hidden transition-all ${dragIndex === i ? "opacity-50 border-zinc-950 border-dashed" : selectMode && selectedItems.has(it.item_id) ? "border-zinc-950 ring-2 ring-zinc-950 cursor-pointer" : selectMode ? "border-zinc-200 hover:border-zinc-400 cursor-pointer" : "border-zinc-200"}`}>
              {selectMode && (
                <span data-testid={`collection-select-${it.item_id}`}
                  className={`absolute top-2 left-2 z-20 w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedItems.has(it.item_id) ? "bg-zinc-950 border-white text-white" : "bg-white border-zinc-300 text-transparent"}`}>
                  <CheckCircle2 size={14} />
                </span>
              )}
              {!selectMode && (
                <span className="absolute top-2 left-2 z-10 bg-white/90 border border-zinc-200 rounded p-1 cursor-grab active:cursor-grabbing text-zinc-400" title="Drag to reorder" data-testid={`collection-drag-handle-${it.item_id}`}>
                  <GripVertical size={14} />
                </span>
              )}
              {it.version_label && (
                <span className="absolute top-2 right-2 z-10 bg-zinc-950 text-white text-[10px] px-2 py-1 rounded font-medium" data-testid={`collection-item-version-${it.item_id}`}>
                  {it.version_label}
                </span>
              )}
              {selectMode ? (
                <img src={imgUrl(it.image)} alt={it.design.title} draggable={false} className="w-full h-80 object-cover pointer-events-none" />
              ) : (
                <Link to={`/designs/${it.design.id}`} draggable={false}>
                  <img src={imgUrl(it.image)} alt={it.design.title} draggable={false} className="w-full h-80 object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                </Link>
              )}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Look {String(i + 1).padStart(2, "0")}</p>
                    <p className="text-sm font-medium mt-0.5 truncate" data-testid={`collection-item-title-${it.item_id}`}>{it.design.product_title || it.design.title}{it.version_label ? ` — ${it.version_label}` : ""}</p>
                  </div>
                  {canManage && (
                    <button data-testid={`collection-remove-item-${it.item_id}`} onClick={(e) => { e.stopPropagation(); removeItem(it.item_id); }} title="Remove from collection"
                      className="p-1.5 rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-red-600 transition-colors"><X size={14} /></button>
                  )}
                </div>
                {it.design.product_description && (
                  <p className="text-[11px] text-zinc-500 mt-2 line-clamp-3 whitespace-pre-line" data-testid={`collection-item-description-${it.item_id}`}>
                    {it.design.product_description}
                  </p>
                )}
                {it.costing ? (
                  <Link to={`/costing/${it.costing.cost_sheet_id}`} data-testid={`collection-item-costing-${it.item_id}`}
                    className="mt-3 grid grid-cols-3 gap-2 pt-3 border-t border-zinc-100 hover:bg-zinc-50 rounded-md -mx-1 px-1 py-1 transition-colors">
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Landed</p>
                      <p className="text-sm font-medium">{it.costing.currency} {it.costing.landed_cost.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">WHSL</p>
                      <p className="text-sm font-medium">{it.costing.currency} {it.costing.wholesale_price.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Retail</p>
                      <p className="text-sm font-medium">{it.costing.currency} {it.costing.retail_price.toFixed(2)}</p>
                    </div>
                  </Link>
                ) : (
                  <p className="mt-3 pt-3 border-t border-zinc-100 text-[11px] text-zinc-400">No cost sheet yet</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <TechpackWizard
        open={showTechpackWizard}
        mode="collection"
        collection={collection}
        onClose={() => setShowTechpackWizard(false)}
        onCreated={() => { setShowTechpackWizard(false); navigate("/techpacks"); }}
      />
      {showPoDialog && (
        <div className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4" data-testid="po-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart size={18} className="text-zinc-950" />
                <h2 className="font-heading text-lg font-bold">Issue Purchase Order</h2>
              </div>
              <button data-testid="po-dialog-close" onClick={() => setShowPoDialog(false)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-zinc-500">{selectedItems.size} style{selectedItems.size !== 1 ? "s" : ""} selected. Enter the supplier — you can fill quantities, sizes and terms on the next screen.</p>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Supplier name *</label>
                <input data-testid="po-supplier-name" autoFocus value={poSupplier.name} onChange={(e) => setPoSupplier({ ...poSupplier, name: e.target.value })}
                  className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                  placeholder="e.g. Atelier Milano Srl" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Contact person</label>
                <input value={poSupplier.contact_person} onChange={(e) => setPoSupplier({ ...poSupplier, contact_person: e.target.value })}
                  className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                  placeholder="e.g. Marco Ferretti" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Address</label>
                <textarea data-testid="po-supplier-address" rows={3} value={poSupplier.address} onChange={(e) => setPoSupplier({ ...poSupplier, address: e.target.value })}
                  className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                  placeholder="Full mailing address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Email</label>
                  <input type="email" value={poSupplier.email} onChange={(e) => setPoSupplier({ ...poSupplier, email: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Phone</label>
                  <input value={poSupplier.phone} onChange={(e) => setPoSupplier({ ...poSupplier, phone: e.target.value })}
                    className="mt-1 w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
              </div>
              <p className="text-[11px] text-zinc-400">Note: unit costs will default to the linked cost sheet&apos;s wholesale price and size ranges come from each techpack.</p>
            </div>
            <div className="px-5 py-3 border-t border-zinc-200 flex items-center justify-end gap-2">
              <button onClick={() => setShowPoDialog(false)} className="text-sm px-4 py-2 rounded-md hover:bg-zinc-100">Cancel</button>
              <button data-testid="po-dialog-submit" onClick={createPo} disabled={creatingPo || !poSupplier.name.trim()}
                className="inline-flex items-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                <ShoppingCart size={14} /> {creatingPo ? "Creating…" : "Create Purchase Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
